import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import type {
  TechnicalDataScenario,
  TechnicalDataScenarioFormState,
  TechnicalDataScenarioModalMode,
} from "../model/types";

import { useTechnicalDataScenarioContext } from "../context/TechnicalDataScenarioProvider";
import { mapScenarioToFormState } from "../lib/scenarioDefaults";

type ScenarioModalState = {
  formState: TechnicalDataScenarioFormState;
  mode: TechnicalDataScenarioModalMode;
  scenarioId: null | number;
};

export const useScenarioSelectorActions = () => {
  const { t } = useTranslation();
  const { addScenario, canDeleteScenario, createEmptyFormState, deleteScenario, setActiveScenarioId, updateScenario } =
    useTechnicalDataScenarioContext();
  const [modalState, setModalState] = useState<null | ScenarioModalState>(null);
  const [scenarioToDelete, setScenarioToDelete] = useState<null | TechnicalDataScenario>(null);

  const openAddModal = () => {
    setModalState({ formState: createEmptyFormState(), mode: "add", scenarioId: null });
  };

  const openEditModal = (scenario: TechnicalDataScenario) => {
    setModalState({ formState: mapScenarioToFormState(scenario), mode: "edit", scenarioId: scenario.id });
  };

  const closeModal = () => setModalState(null);

  const handleSubmit = async (formState: TechnicalDataScenarioFormState) => {
    if (!modalState) return;
    if (modalState.mode === "add") {
      await addScenario(formState);
      return;
    }
    if (modalState.scenarioId == null) return;
    await updateScenario(modalState.scenarioId, formState);
  };

  const handleDelete = (scenario: TechnicalDataScenario) => {
    if (!canDeleteScenario(scenario)) {
      toast.error(t("TechnicalData.scenario.baselineDeleteError"));
      return;
    }
    setScenarioToDelete(scenario);
  };

  const closeDeleteConfirm = () => setScenarioToDelete(null);

  const confirmDelete = () => {
    if (!scenarioToDelete) return;
    deleteScenario(scenarioToDelete.id);
    setScenarioToDelete(null);
  };

  const modalInitialState = useMemo(
    () => modalState?.formState ?? createEmptyFormState(),
    [createEmptyFormState, modalState?.formState]
  );

  return {
    closeDeleteConfirm,
    closeModal,
    confirmDelete,
    handleDelete,
    handleSubmit,
    modalInitialState,
    modalState,
    openAddModal,
    openEditModal,
    scenarioToDelete,
    setActiveScenarioId,
  };
};
